import React, { useReducer, useState } from 'react';

import { FlexColumn } from '@editor/components/utilities/layouts';
import { withEventScope } from '@repo/event-processor';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';

import TextInput from '../../../TextInput/TextInput';
import { borderLessInputTheme } from '../../../TextInput/theme';
import Variation from '../../../Variation/Variation';
import { OffboardingReasonId } from '../config';
import {
  ResponsiveHeader,
  ResponsiveText,
  ResponsiveButtonsWrapper,
} from '../styles';

import { QuestionnaireContentProps } from './types';

const QuestionnaireContent: React.FC<QuestionnaireContentProps> = ({
  reasons,
  header,
  primaryButton,
  secondaryButton,
}) => {
  const [activeReasonId, setActiveReasonId] = useState<OffboardingReasonId>(
    reasons[0].id
  );
  const [extraInput, setExtraInput] = useReducer<
    React.Reducer<
      Partial<Record<OffboardingReasonId, string>>,
      { reasonId: OffboardingReasonId; input: string }
    >
  >(
    (state, action) => ({
      ...state,
      [action.reasonId]: action.input,
    }),
    {}
  );

  const handleReasonClick = (id: OffboardingReasonId): void => {
    setActiveReasonId(id);
  };

  const handleNextClick = (): void => {
    primaryButton.onClick({
      reasonId: activeReasonId,
      extraInput: extraInput[activeReasonId] ?? null,
    });
  };

  return (
    <>
      <ResponsiveHeader
        style={{ textAlign: 'center', wordBreak: 'break-word' }}
      >
        {header}
      </ResponsiveHeader>
      <Spacer height="12px" />
      <ResponsiveText style={{ textAlign: 'center' }}>
        This helps us to understand how we can better shape our offering and
        improve our features.
      </ResponsiveText>
      <Spacer height="32px" />
      <FlexColumn gap="16px" width="100%">
        {reasons.map((reason) => {
          const isActive = reason.id === activeReasonId;
          const shouldShowExtraInput =
            'hasExtraInput' in reason && reason.hasExtraInput && isActive;

          return (
            <Variation
              key={reason.id}
              isActive={isActive}
              label={reason.label}
              onClick={(): void => handleReasonClick(reason.id)}
            >
              {shouldShowExtraInput && (
                <TextInput
                  isTextArea
                  theme={{
                    ...borderLessInputTheme,
                    backgroundColor: themeStyle.varBackgroundAlt,
                    height: '69px',
                  }}
                  value={extraInput[reason.id]}
                  onChanging={(input): void =>
                    setExtraInput({ reasonId: reason.id, input })
                  }
                />
              )}
            </Variation>
          );
        })}
      </FlexColumn>
      <Spacer height="24px" />
      <ResponsiveButtonsWrapper>
        {secondaryButton && (
          <Button
            label={secondaryButton.label}
            name="secondaryQuestionnaireButton"
            theme={secondaryTheme}
            width="100%"
            onClick={secondaryButton.onClick}
          />
        )}
        <Button
          label={primaryButton.label}
          name="nextQuestionnaireButton"
          width="100%"
          onClick={handleNextClick}
        />
      </ResponsiveButtonsWrapper>
    </>
  );
};

export default withEventScope(QuestionnaireContent, {
  module: 'questionnaireContent',
});
